import React, { useState } from "react";
import {
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  tokens,
} from "@fluentui/react-components";
import { ChevronDown12Regular, Edit16Regular } from "@fluentui/react-icons";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { InlineText } from "./InlineText";

const useStyles = makeStyles({
  memberTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    background: "none",
    border: "none",
    padding: "2px 4px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    color: "inherit",
    transition: "background-color 0.15s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "@media print": {
      padding: 0,
      margin: 0,
      backgroundColor: "transparent !important",
      cursor: "default !important",
    },
  },
  readOnly: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 4px",
  },
  caret: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    "@media print": {
      display: "none !important",
    },
  },
});

export interface InlineMemberPopoverProps {
  value: string;
  options: string[];
  onChange: (newValue: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function InlineMemberPopover({
  value,
  options,
  onChange,
  readOnly = false,
  placeholder = "담당자 선택",
  ariaLabel,
  className,
  style,
}: InlineMemberPopoverProps) {
  const styles = useStyles();
  const [isCustomEditing, setIsCustomEditing] = useState(false);

  if (isCustomEditing) {
    return (
      <InlineText
        value={value}
        onCommit={(next) => {
          setIsCustomEditing(false);
          onChange(next.trim() || "—");
        }}
        onCancel={() => setIsCustomEditing(false)}
        placeholder={placeholder}
        className={className}
        style={style}
        ariaLabel={ariaLabel}
        autoSelect
        isInitialEditing
      />
    );
  }

  const isEmpty = !value || value === "—";
  const displayLabel = isEmpty ? placeholder : value;

  if (readOnly) {
    return (
      <span className={`${styles.readOnly} ${className || ""}`} style={style} aria-label={ariaLabel}>
        {!isEmpty ? <PersonAvatar name={value} size={16} tip={false} /> : null}
        <span>{displayLabel}</span>
      </span>
    );
  }

  // Filter out empty or duplicate entries
  const cleanOptions = Array.from(new Set(options.map((o) => o.trim()).filter((o) => o && o !== "—")));

  return (
    <Menu positioning="below-start">
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={`${styles.memberTrigger} ${className || ""}`}
          style={style}
          aria-label={ariaLabel || displayLabel}
        >
          {!isEmpty ? <PersonAvatar name={value} size={16} tip={false} /> : null}
          <span>{displayLabel}</span>
          <span className={styles.caret} data-print-hide="">
            <ChevronDown12Regular />
          </span>
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {cleanOptions.map((opt) => (
            <MenuItem
              key={opt}
              icon={<PersonAvatar name={opt} size={20} tip={false} />}
              onClick={() => onChange(opt)}
            >
              {opt}
            </MenuItem>
          ))}
          <MenuItem
            icon={<Edit16Regular />}
            onClick={() => setIsCustomEditing(true)}
          >
            직접 입력
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
